/*
 * Converts the free-shipping threshold into the shopper's currency.
 *
 * The merchant types one number, in the store's currency. Liquid compares it
 * against cart.total_price, which arrives already converted into whatever
 * currency the shopper is browsing in — so without this the number would be
 * reinterpreted rather than converted, and $100 would become 100 of a currency
 * worth a fraction as much.
 *
 * Shopify publishes the shop-to-presentment rate as Shopify.currency.rate and
 * exposes it nowhere in Liquid, which is why this is the one part of the bar
 * that has to happen on the client. When the rate is 1 nothing here runs at all
 * and the server-rendered figures stand.
 *
 * This only changes what the bar SAYS. Free shipping itself is granted by the
 * shipping rate in the Shopify admin, which this cannot see.
 */
if (!customElements.get('shipping-progress')) {
  class ShippingProgress extends HTMLElement {
    connectedCallback() {
      this.request?.abort();
      this.request = null;
      const rate = this.rate();
      if (rate === 1) return;

      const threshold = Math.round((parseInt(this.dataset.threshold, 10) || 0) * rate);
      if (!threshold) return;

      this.message = this.querySelector('.ship-progress__message');
      this.fill = this.querySelector('.ship-progress__fill');
      if (!this.message) return;

      const request = new AbortController();
      this.request = request;
      fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { signal: request.signal })
        .then((response) => {
          if (!response.ok) throw new Error('Cart unavailable');
          return response.json();
        })
        .then((cart) => {
          if (this.isConnected && this.request === request) this.render(threshold, cart.total_price, cart.currency);
        })
        .catch(() => {
          // The server-rendered figure was true for the store currency when the
          // page loaded. Leaving it is a smaller error than blanking the bar.
        });
    }

    disconnectedCallback() {
      this.request?.abort();
      this.request = null;
    }

    /* 1 when the shopper is already in the store's own currency, in which case
       the server-rendered figure is right and converting it would be the bug. */
    rate() {
      const rate = parseFloat(window.Shopify?.currency?.rate);
      return Number.isFinite(rate) && rate > 0 ? rate : 1;
    }

    format(cents, currency) {
      if (!currency) return null;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency,
        }).format(cents / 100);
      } catch (error) {
        return null;
      }
    }

    render(threshold, total, currency) {
      const remaining = threshold - total;
      const progress = Math.max(0, Math.min(100, Math.round((total * 100) / threshold)));
      if (this.fill) this.fill.style.setProperty('--shipping-progress', `${progress}%`);

      const icon = this.message.querySelector('.svg-wrapper');
      const thresholdMoney = this.format(threshold, currency);
      if (!thresholdMoney) return;
      const zero = this.querySelector('[data-shipping-zero]');
      const endpoint = this.querySelector('[data-shipping-threshold]');
      if (zero) zero.textContent = this.format(0, currency);
      if (endpoint) endpoint.textContent = thresholdMoney;
      const interpolate = (text, amount = '') => text.replaceAll('[amount]', amount).replaceAll('{amount}', amount).replaceAll('{threshold}', thresholdMoney);

      if (remaining > 0) {
        const amount = this.format(remaining, currency);
        if (!amount) return;
        this.setText(icon, interpolate(this.dataset.textRemaining || '', amount));
      } else {
        this.setText(icon, interpolate(this.dataset.textQualified || ''));
      }
    }

    /* Text nodes rather than innerHTML: the strings are merchant-editable
       translations, and append() with a string cannot introduce markup. The
       icon is kept as the element it already is rather than re-serialised. */
    setText(icon, text) {
      this.message.textContent = '';
      if (icon) this.message.appendChild(icon);
      this.message.append(` ${text}`);
    }
  }

  customElements.define('shipping-progress', ShippingProgress);
}
